import type { components } from './schema.gen';

type Problem = components['schemas']['Problem'];

/** Failures the app knows how to explain, the same cases as the mobile app's AppException. */
export type ErrorKind =
  | 'network'
  | 'unauthorized'
  | 'invalidCredentials'
  | 'forbidden'
  | 'notFound'
  | 'stale'
  | 'conflict'
  | 'alreadyHandedOff'
  | 'validation'
  | 'notAllowed'
  | 'invalidEmployee'
  | 'rateLimited'
  | 'server'
  | 'cancelled';

export class AppError extends Error {
  readonly kind: ErrorKind;
  readonly detail?: string;
  readonly fields: Record<string, string>;
  /** For `alreadyHandedOff`: the task already going. */
  readonly existingItemId?: string;
  /** The problem's stable code (`already_hired`, `answers_required`), for screens that explain it. */
  readonly code?: string;

  constructor(
    kind: ErrorKind,
    detail?: string,
    extra: { fields?: Record<string, string>; existingItemId?: string; code?: string } = {},
  ) {
    super(detail ?? kind);
    this.name = 'AppError';
    this.kind = kind;
    this.detail = detail;
    this.fields = extra.fields ?? {};
    this.existingItemId = extra.existingItemId;
    this.code = extra.code;
  }
}

/** Maps an RFC 9457 problem (or its absence) to an AppError, as the mobile app's mapDioException does. */
export function errorFromResponse(status: number, body: unknown): AppError {
  const p = (typeof body === 'object' && body !== null ? body : {}) as Partial<Problem>;
  const detail = p.detail ?? undefined;
  const code = p.code ?? undefined;
  const fields = Object.fromEntries((p.errors ?? []).map((f) => [f.field, f.message]));
  const error = (kind: ErrorKind, d = detail, extra: { fields?: Record<string, string>; existingItemId?: string } = {}) =>
    new AppError(kind, d, { ...extra, code });
  switch (status) {
    case 400:
      return error('validation', detail, { fields });
    case 422:
      if (code === 'not_allowed') return error('notAllowed', detail, { fields });
      if (code === 'invalid_employee') return error('invalidEmployee', detail, { fields });
      return error('validation', detail, { fields });
    case 401:
      return error(code === 'invalid_credentials' ? 'invalidCredentials' : 'unauthorized');
    case 403:
      return error('forbidden');
    case 404:
      return error('notFound');
    case 409:
      if (code === 'already_handed_off' && p.existing_item_id) {
        return error('alreadyHandedOff', detail, { existingItemId: p.existing_item_id });
      }
      return error('conflict');
    case 412:
    case 428:
      return error('stale');
    case 429:
      return error('rateLimited');
    default:
      return error('server', detail ?? `HTTP ${status}`);
  }
}

/** Anything thrown while talking to the API, as an AppError. */
export function toAppError(e: unknown): AppError {
  if (e instanceof AppError) return e;
  // fetch rejects with a TypeError when there is no connection.
  if (e instanceof TypeError) return new AppError('network', e.message);
  return new AppError('server', e instanceof Error ? e.message : String(e));
}
